import { Card, Empty, Flex, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { type ReactNode } from "react"

// The shared wrapper, not antd's Table: it is what binds the scroll listener
// the hook below hands it.
import Table from "../../../../components/Table"
import { TableResultCount } from "../../../../components/Table/TableResultCount.tsx"
import { useInfiniteScroll } from "../../../../hooks/use-table-infinite-scroll.tsx"

const { Title } = Typography

interface CustomerRelatedCardProps<T> {
  testId: string
  title: string
  emptyText: string
  columns: ColumnsType<T>
  rows: T[]
  count: number
  next: string | null
  retryNext?: string | null
  loading?: boolean
  extra?: ReactNode
  onLoadMore: () => void
}

/** An empty related list is a neutral state, never an error reading. */
function CustomerRelatedCard<T extends { id: string }>({
  testId,
  title,
  emptyText,
  columns,
  rows,
  count,
  next,
  retryNext,
  loading,
  extra,
  onLoadMore
}: CustomerRelatedCardProps<T>) {
  const { onTableScroll, containerRef } = useInfiniteScroll({
    loading: !!loading,
    hasMore: !!next,
    loadMoreKey: next,
    loadMore: onLoadMore
  })

  return (
    <Card size={"small"} data-testid={testId} ref={containerRef}>
      <Flex vertical gap={"small"}>
        <Flex align={"center"} justify={"space-between"} gap={"small"}>
          <Title level={5} className={"m-0"}>
            {title}
          </Title>
          {extra}
        </Flex>

        <Table<T>
          size={"small"}
          rowKey={"id"}
          columns={columns}
          dataSource={rows}
          loading={!!loading && !rows?.length}
          // Fixed, and one of the two exemptions from the measured height:
          // two of these cards stack inside one scrolling column, so a body
          // that fills the space below it would take the whole scrollport
          // and leave the second card measuring from off-screen.
          scroll={{ x: true, y: 320 }}
          onScroll={onTableScroll}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={emptyText}
              />
            )
          }}
          withOutPagination
          pagination={false}
        />
        <TableResultCount
          loaded={rows?.length ?? 0}
          total={count}
          loading={!!loading}
          next={next}
          retryNext={retryNext}
          onRetry={onLoadMore}
        />
      </Flex>
    </Card>
  )
}

export { CustomerRelatedCard }
